/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Table as AntdTable,
  TableColumnsType,
} from 'antd';
import {
  FC, ReactElement, useEffect, useMemo, useRef,
  useState,
} from 'react';
import _ from 'lodash';
import { ColumnType as AntdColumnType } from 'antd/lib/table';
import { DataStateItem } from 'shared/types';
import { IListColumnMeta } from 'shared/types/Page.interface';
import { TableHeader } from './ui/TableHeader';
import { TableAction, TableActions } from './ui/TableActions/TableActions';
import { TableColumnCell } from './ui/TableColumn/TableColumnCell';
import { prepareColumnFilters } from './lib/columnFilters';
import { filterDataSource } from './lib/fullTableSearch';
import { getColumnSorter } from './lib/columnSorters';
import { Filters, Scroll, Sorts } from './lib/types';

interface ColumnType<T> extends AntdColumnType<T> {}

export interface TableProps {
    columns: IListColumnMeta
    dataSource: any[]
    scroll?: Scroll
    onRowDoubleClick?: (record?: DataStateItem) => void;
    tableHeaderButtons?: ReactElement[];
    actions?: TableAction[]
    hasPagination?: boolean
    enableFullTableSearch?: boolean
    enableClearFilters?: boolean
    rowKey?: string
  }

export const Table:FC<TableProps> = (props: TableProps) => {
  const {
    columns,
    dataSource,
    onRowDoubleClick,
    tableHeaderButtons,
    enableFullTableSearch,
    enableClearFilters,
    actions,
    scroll,
    rowKey,
    hasPagination = true,
  } = props;

  const tableRef = useRef<HTMLDivElement>(null);
  const [fullTableSearch, setFullTableSearch] = useState<string>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeFilters, setActiveFilters] = useState<Filters>({});
  const [activeSorters, setActiveSorters] = useState<Sorts>({});

  const preparedDataSource: DataStateItem[] = useMemo(
    () => filterDataSource(dataSource as any[], fullTableSearch, columns),
    [dataSource, fullTableSearch, columns],
  );

  const preparedColumns: TableColumnsType<DataStateItem> = useMemo(() => {
    const columnsWithFilterAndSorter = columns.map((item) => ({
      ...item,
      sorter: getColumnSorter(item),
      render: (value: string, record: DataStateItem) => <TableColumnCell record={record} value={value} columnMeta={item} />,
      ...prepareColumnFilters(item, preparedDataSource, activeFilters),
    }));
    const actionsColumn: ColumnType<DataStateItem> = {
      title: '',
      dataIndex: '',
      width: '10px',
      key: 'x',
      fixed: scroll ? 'right' : null,
      render: (_, record: DataStateItem) => (
        <TableActions actions={actions} record={record} />
      ),
    };
    return actions ? [...columnsWithFilterAndSorter, actionsColumn] : columnsWithFilterAndSorter;
  }, [columns, preparedDataSource, activeFilters]);

  const handleFiltersChange = (newPagination: any, filters: any, sorters: any) => {
    if (!_.isEqual(sorters, activeSorters)) {
      setActiveSorters(sorters);
    }
    if (!_.isEqual(filters, activeFilters)) {
      setActiveFilters(filters);
    }
  };

  useEffect(() => {
    if (activeSorters) setCurrentPage(1);
  }, [activeSorters]);

  return (
    <div ref={tableRef}>
      <TableHeader
        tableHeaderButtons={tableHeaderButtons}
        onFullTableSearch={enableFullTableSearch ? setFullTableSearch : null}
        activeFilters={activeFilters}
        onClearFilters={() => setActiveFilters({})}
        enableClearFilters={enableClearFilters}
      >
        <AntdTable
          rowKey={rowKey || 'id'}
          columns={preparedColumns}
          dataSource={preparedDataSource}
          onRow={(record: DataStateItem) => ({
            onDoubleClick: () => onRowDoubleClick?.(record),
          })}
          scroll={scroll}
          onChange={handleFiltersChange}
          pagination={hasPagination
            && {
              position: ['bottomCenter'],
              showTotal: (total) => `Количество строк: ${total}`,
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
            }}
        />
      </TableHeader>
    </div>
  );
};
